import { useState, useEffect, useMemo } from "react";
import SbuxCard from "../../../components/SbuxCard";
import SbuxDrawer from "../../../components/SbuxDrawer";
import { useAppDispatch, useAppSelector } from "../../../store/types";
import FeatureFlagsTable from "./table/featureFlags/FeatureFlagsTable";
import ResourceManagerTable from "./table/resourceManager/ResourceManagerTable";
import MainHoursMaterTable from "../admin/table/mainHours/MainHoursMaterTable";
import MediaMgtTabs from "./table/mediaMgt/MediaMgtTabs";
import ClosureMsgTabs from "./table/closureMsg/ClosureMsgTabs";
import DataManagementTabs from "./table/dataMgt/DataManagementTabs";
import LexBotsTable from "./table/lexBots/LexBotsTable";
import TemporaryMsgTabs from "./table/temporaryMsg/TemporaryMsgTabs";
import StopContactTable from "./table/stopContact/StopContactTable";
import ConnectTable from "./table/connectMgt/ConnectTable";
import TestSms from "./table/testSms/TestSms";
import { getFeatureFlags } from "../../../services/featureFlags";
import {
  getClosureMsgFilterItem,
  getTemporaryMsgFilterItem,
  getMediaMgtFilterItem,
} from "../../../services/filter";
import {
  getMediaMgt,
  getResetMediaMgt,
  getMediaExpandCollapseInfo,
} from "../../../services/mediaMgt";

import {
  getClosureMsg,
  getResetClosureMsg,
} from "../../../services/closureMsg";

import {
  getTemporaryMsg,
  getResetTemporaryMsg,
} from "../../../services/temporaryMsg";
import { getResetDataManagement } from "../../../services/dataMgt";
import { resetLexBots, resetLexBotDetail } from "../../../services/lexBots";

import { resetApiData } from "../../../services/connect";

import SbuxFallback from "../../../components/SbuxFallback";
import { ErrorBoundary } from "react-error-boundary";
import { logMessage } from "../../../utils/amplifyLogger";
import { clientLog } from "../../../services/logging";
import { panelData } from "./common/panelData";
import SbuxStackSnackbar from "../../../components/SbuxStackSnackbar";
import {
  PLEASE_WAIT,
  SNACK_BARS_SEVERITY_ERROR,
  OOPS_ERROR_MESSAGE,
  MEDIAMGT_SAVED_SUCCESSFULLY,
  MEDIAMGT_DELETED_SUCCESSFULLY,
  CLOSUREMSG_SAVED_SUCCESSFULLY,
  MEDIAMGT_ERROR_MESSAGE,
  CLOSUREMSG_DELETED_SUCCESSFULLY,
  TEMPORARYMSG_SAVED_SUCCESSFULLY,
  CLOSUREMSG_DEFAULT_FILTER_ITEMS,
  TEMPEMSG_DEFAULT_FILTER_ITEMS,
  MEDIAMSG_DEFAULT_FILTER_ITEMS,
  LOADING_DATA,
  ROUTING_PROFILE_DELETION_SUCCESSFUL,
  DATA_MANAGEMENT_SAVED_SUCCESSFULLY,
  DATA_MANAGEMENT_DELETED_SUCCESSFULLY,
  CALLBACK_DELETED_SUCCESSFULLY,
  ADMIN_HOURS_ERROR_MESSAGE,
  ADMIN_HOURS_SAVED_SUCCESSFULLY,
} from "../../../constants";
import { Box } from "@mui/material";
import * as globalConfigSelectors from "../../../selectors/globalConfigSelectors";
import * as settingsSelectors from "../../../selectors/settingsSelector";
import * as userInstanceSelectors from "../../../selectors/userInstanceSelector";
import * as featureFlagSelectors from "../../../selectors/featureFlagSelector";
import * as mediaMgtSelectors from "../../../selectors/mediaMgtSelector";
import * as closureMsgSelectors from "../../../selectors/closureMsgSelector";
import * as temporaryMsgSelectors from "../../../selectors/temporaryMsgSelector";
import * as dataMgtSelectors from "../../../selectors/dataMgtSelector";
import * as filterSelectors from "../../../selectors/filterSelector";
import * as stopContactSelectors from "../../../selectors/stopContactSelector";
import * as connectSelectors from "../../../selectors/connectSelector";
import {
  getLexBotsLoadingSelector,
  getLexBotsIsErrorSelector,
  getLexBotsErrorMessageSelector,
} from "../../../selectors/lexBotsSelector";
import {
  getMainHoursDataSelector,
  getMainHoursIsSaveSelector,
  getMainHoursIsErrorSelector,
  getMainHoursStatusSelector,
} from "../../../selectors/mainHoursSelector";
import { useSnackbars } from "../../../hooks/useSnackbar";
import { getMainHours } from "../../../services/mainHours";
import { getStopContactData } from "../../../services/stopContact";
import SbuxTranslate from "../../../components/SbuxTranslate";

const Manager = () => {
  const dispatch = useAppDispatch();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbars();

  const globalConfigUi = useAppSelector(
    globalConfigSelectors.getGlobalConfigUiSelector,
  );
  const admin = useAppSelector(settingsSelectors.getAdminSelector);
  const managerNav = useAppSelector(settingsSelectors.getManagerNavSelector);
  // User Instance Selectors
  const connectedInstance = useAppSelector(
    userInstanceSelectors.getConnectedInstanceSelector,
  );
  const languages = useAppSelector(userInstanceSelectors.getLanguagesSelector);
  const business = useAppSelector(userInstanceSelectors.getBusinessSelector);
  const closureTabs = useAppSelector(
    userInstanceSelectors.getClosureTabsSelector,
  );

  // Feature Flags Selectors
  const featureFlags = useAppSelector(featureFlagSelectors.selectFeatureFlags);
  const status = useAppSelector(
    featureFlagSelectors.getFeatureFlagsStatusSelector,
  );

  // Media Management Selectors
  const mediaMgtData = useAppSelector(
    mediaMgtSelectors.getMediaMgtDataSelector,
  );
  const isMMPending = useAppSelector(mediaMgtSelectors.getMMPendingSelector);
  const isMMSave = useAppSelector(mediaMgtSelectors.getMMSaveSelector);
  const isMMDelete = useAppSelector(mediaMgtSelectors.getMMDeleteSelector);
  const isMMError = useAppSelector(mediaMgtSelectors.getMMErrorSelector);
  const mediaExpandCollapseInfo = useAppSelector(
    mediaMgtSelectors.getMediaExpandCollapseInfoSelector,
  );

  // Closure Messages Selectors
  const closureMsgData = useAppSelector(
    closureMsgSelectors.getClosureMsgDataSelector,
  );
  const isCMPending = useAppSelector(
    closureMsgSelectors.getClosureMsgPendingSelector,
  );
  const isCMSave = useAppSelector(
    closureMsgSelectors.getClosureMsgSaveSelector,
  );
  const isCMDelete = useAppSelector(
    closureMsgSelectors.getClosureMsgDeleteSelector,
  );
  const isCMError = useAppSelector(
    closureMsgSelectors.getClosureMsgErrorSelector,
  );

  // Temporary Messages Selectors
  const temporaryMsgData = useAppSelector(
    temporaryMsgSelectors.getTemporaryMsgDataSelector,
  );
  const isTMPending = useAppSelector(
    temporaryMsgSelectors.getTemporaryMsgPendingSelector,
  );
  const isTMError = useAppSelector(
    temporaryMsgSelectors.getTemporaryMsgErrorSelector,
  );
  const isTMSave = useAppSelector(
    temporaryMsgSelectors.getTemporaryMsgSaveSelector,
  );

  // Data Management Selectors
  const isDMPending = useAppSelector(
    dataMgtSelectors.getDataMgtPendingSelector,
  );
  const isDMSave = useAppSelector(dataMgtSelectors.getDataMgtSaveSelector);
  const isDMDelete = useAppSelector(dataMgtSelectors.getDataMgtDeleteSelector);
  const isDMError = useAppSelector(dataMgtSelectors.getDataMgtErrorSelector);

  // Filter Selectors
  const closureMsgFilterItem = useAppSelector(
    filterSelectors.getClosureMsgFilterItemSelector,
  );
  const temporaryMsgFilterItem = useAppSelector(
    filterSelectors.getTemporaryMsgFilterItemSelector,
  );
  const mediaMgtFilterItem = useAppSelector(
    filterSelectors.getMediaMgtFilterItemSelector,
  );
  const closureMsgReset = useAppSelector(
    filterSelectors.getClosureMsgResetSelector,
  );
  const temporaryMsgReset = useAppSelector(
    filterSelectors.getTemporaryMsgResetSelector,
  );
  const mediaMgtReset = useAppSelector(
    filterSelectors.getMediaMgtResetSelector,
  );

  // Stop Contact Selectors
  const isSCPending = useAppSelector(
    stopContactSelectors.getStopContactPendingSelector,
  );
  const isSCError = useAppSelector(
    stopContactSelectors.getStopContactErrorSelector,
  );
  const isSCDelete = useAppSelector(
    stopContactSelectors.getStopContactDeleteSelector,
  );

  // Connect Selectors
  const connectStatus = useAppSelector(
    connectSelectors.getConnectStatusSelector,
  );
  const connectErrorMessage = useAppSelector(
    connectSelectors.getConnectErrorMessageSelector,
  );

  // Lex Bots Selectors
  const isLexBotsLoading = useAppSelector(getLexBotsLoadingSelector);
  const isLexBotsError = useAppSelector(getLexBotsIsErrorSelector);
  const lexBotsErrorMessage = useAppSelector(getLexBotsErrorMessageSelector);

  // Remove individual snackbar states and keep only necessary UI states
  const [openMediaMgt, setOpenMediaMgt] = useState(false);
  const [openClosureMsg, setOpenClosureMsg] = useState(false);
  const [openTemporaryMsg, setOpenTemporaryMsg] = useState(false);
  const [showOtherIndicator, setShowOtherIndicator] = useState(false);
  const [featureFlagsData, setFeatureFlagsData] = useState<
    { name: string; enabled: boolean; [key: string]: unknown }[]
  >([]);
  const [mainHoursData, setMainHoursDataData] = useState<unknown[]>([]);
  const [selectedIndicator, setSelectedIndicator] = useState<
    Record<string, boolean>
  >({});
  const [selectedName, setSelectedName] = useState<string | null>(null);

  const mainHours = useAppSelector(getMainHoursDataSelector);
  const isSave = useAppSelector(getMainHoursIsSaveSelector);
  const isError = useAppSelector(getMainHoursIsErrorSelector);
  const mainHoursStatus = useAppSelector(getMainHoursStatusSelector);

  // Add cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Reset all states when component unmounts
      setOpenMediaMgt(false);
      setOpenClosureMsg(false);
      setOpenTemporaryMsg(false);
      hideSnackbar();
      // Reset all data
      dispatch(getResetMediaMgt());
      dispatch(getResetClosureMsg());
      dispatch(getResetTemporaryMsg());
      dispatch(resetApiData());
      dispatch(getResetDataManagement());
      dispatch(resetLexBots());
    };
  }, []);

  // Modify the initial effect to not reset data on mount
  useEffect(() => {
    setOpenMediaMgt(false);
    setOpenClosureMsg(false);
    setOpenTemporaryMsg(false);
    hideSnackbar();
  }, []);

  // Add effect to handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Reset all states when tab becomes hidden
        setOpenMediaMgt(false);
        setOpenClosureMsg(false);
        setOpenTemporaryMsg(false);
        hideSnackbar();
      }
    };
    dispatch(getFeatureFlags());
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (featureFlags && featureFlags.length) {
      setFeatureFlagsData(
        featureFlags.filter(
          (ff) => !ff.doesFeatureFlagBelongToAdminControls,
        ) as unknown as {
          name: string;
          enabled: boolean;
          [key: string]: unknown;
        }[],
      );
    }
  }, [featureFlags]);

  useEffect(() => {
    if (mainHours && mainHours.length) {
      setMainHoursDataData(
        mainHours.filter(
          (mainHour) => (mainHour as Record<string, unknown>).showForManagers,
        ),
      );
    }
  }, [mainHours]);

  // Media Management effect - only trigger on value changes to true
  useEffect(() => {
    if (isMMPending) {
      showSnackbar(PLEASE_WAIT);
    }
  }, [isMMPending, showSnackbar]);

  useEffect(() => {
    if (isMMError) {
      showSnackbar(MEDIAMGT_ERROR_MESSAGE, SNACK_BARS_SEVERITY_ERROR);
      dispatch(getResetMediaMgt());
    }
  }, [isMMError, dispatch, showSnackbar]);

  useEffect(() => {
    if (isMMSave) {
      showSnackbar(MEDIAMGT_SAVED_SUCCESSFULLY);
      dispatch(getResetMediaMgt());
    }
  }, [isMMSave, dispatch, showSnackbar]);

  useEffect(() => {
    if (isMMDelete) {
      showSnackbar(MEDIAMGT_DELETED_SUCCESSFULLY);
      dispatch(getResetMediaMgt());
    }
  }, [isMMDelete, dispatch, showSnackbar]);

  // Closure Messages effects
  useEffect(() => {
    if (isCMPending) {
      showSnackbar(PLEASE_WAIT);
    }
  }, [isCMPending, showSnackbar]);

  useEffect(() => {
    if (isCMError) {
      showSnackbar(MEDIAMGT_ERROR_MESSAGE, SNACK_BARS_SEVERITY_ERROR);
    }
  }, [isCMError, showSnackbar]);

  useEffect(() => {
    if (isCMSave) {
      showSnackbar(CLOSUREMSG_SAVED_SUCCESSFULLY);
    }
  }, [isCMSave, showSnackbar]);

  useEffect(() => {
    if (isCMDelete) {
      showSnackbar(CLOSUREMSG_DELETED_SUCCESSFULLY);
    }
  }, [isCMDelete, showSnackbar]);

  // Temporary Messages effects
  useEffect(() => {
    if (isTMPending) {
      showSnackbar(PLEASE_WAIT);
    }
  }, [isTMPending, showSnackbar]);

  useEffect(() => {
    if (isTMError) {
      showSnackbar(MEDIAMGT_ERROR_MESSAGE, SNACK_BARS_SEVERITY_ERROR);
    }
  }, [isTMError, showSnackbar]);

  useEffect(() => {
    if (isTMSave) {
      showSnackbar(TEMPORARYMSG_SAVED_SUCCESSFULLY);
    }
  }, [isTMSave, showSnackbar]);

  // Data Management effects
  useEffect(() => {
    if (isDMPending) {
      showSnackbar(PLEASE_WAIT);
      setOpenMediaMgt(true);
    }
  }, [isDMPending, showSnackbar]);

  useEffect(() => {
    if (isDMError) {
      showSnackbar(MEDIAMGT_ERROR_MESSAGE, SNACK_BARS_SEVERITY_ERROR);
      setOpenMediaMgt(false);
    }
  }, [isDMError, showSnackbar]);

  useEffect(() => {
    if (isDMSave) {
      showSnackbar(DATA_MANAGEMENT_SAVED_SUCCESSFULLY);
      setOpenMediaMgt(false);
    }
  }, [isDMSave, showSnackbar]);

  useEffect(() => {
    if (isDMDelete) {
      showSnackbar(DATA_MANAGEMENT_DELETED_SUCCESSFULLY);
      setOpenMediaMgt(false);
    }
  }, [isDMDelete, showSnackbar]);

  // Stop Contact effects
  useEffect(() => {
    if (isSCPending) {
      showSnackbar(PLEASE_WAIT);
    }
  }, [isSCPending, showSnackbar]);

  useEffect(() => {
    if (isSCError) {
      showSnackbar(OOPS_ERROR_MESSAGE, SNACK_BARS_SEVERITY_ERROR);
    }
  }, [isSCError, showSnackbar]);

  useEffect(() => {
    if (isSCDelete) {
      showSnackbar(CALLBACK_DELETED_SUCCESSFULLY);
    }
  }, [isSCDelete, showSnackbar]);

  // Connect effect
  useEffect(() => {
    if (connectStatus === "get:pending" || connectStatus === "delete:pending") {
      showSnackbar(
        connectStatus === "get:pending" ? LOADING_DATA : PLEASE_WAIT,
      );
    } else if (connectStatus?.endsWith("rejected")) {
      showSnackbar(connectErrorMessage, SNACK_BARS_SEVERITY_ERROR);
    } else if (connectStatus === "delete:success") {
      showSnackbar(ROUTING_PROFILE_DELETION_SUCCESSFUL);
    }
  }, [connectStatus, connectErrorMessage, showSnackbar]);

  // Main Hours effect
  useEffect(() => {
    if (isError) {
      showSnackbar(ADMIN_HOURS_ERROR_MESSAGE, SNACK_BARS_SEVERITY_ERROR);
    } else if (isSave) {
      showSnackbar(ADMIN_HOURS_SAVED_SUCCESSFULLY);
    }
  }, [isError, isSave, showSnackbar]);

  // Lex Bots effects
  useEffect(() => {
    if (isLexBotsLoading) {
      showSnackbar(LOADING_DATA);
    }
  }, [isLexBotsLoading, showSnackbar]);

  useEffect(() => {
    if (isLexBotsError) {
      showSnackbar(
        lexBotsErrorMessage || OOPS_ERROR_MESSAGE,
        SNACK_BARS_SEVERITY_ERROR,
      );
    }
  }, [isLexBotsError, lexBotsErrorMessage, showSnackbar]);

  const handleError = (error: Error) => {
    clientLog({
      component: `Manager`,
      message: `Something went wrong in Manager Component => ${error}`,
    });
    logMessage(
      `Unhandle Error in Manager`,
      `Something went wrong in Manager Component => ${error}`,
      `error`,
    );
  };

  const handleOtherClick = () => {
    setSelectedIndicator({});
    setShowOtherIndicator(true);
    setOpenMediaMgt(false);
    setOpenClosureMsg(false);
    setOpenTemporaryMsg(false);
  };

  const handleRefrehClick = async () => {
    openMediaMgt
      ? await dispatch(getMediaMgt())
      : openClosureMsg
        ? await dispatch(getClosureMsg())
        : openTemporaryMsg
          ? await dispatch(getTemporaryMsg())
          : selectedName === "Main Hours"
            ? await dispatch(getMainHours())
            : await dispatch(getFeatureFlags());
  };

  const handleItemClick = (name: string) => {
    // Store the previous component name before updating
    const previousComponent = selectedName;

    // Explicitly close snackbar by setting open to false
    hideSnackbar();

    // Update to new component
    setSelectedName(name);
    setSelectedIndicator({ [name]: true });

    // Reset UI states when switching tabs
    setOpenMediaMgt(false);
    setOpenClosureMsg(false);
    setOpenTemporaryMsg(false);

    // Reset Redux states based on previous component
    if (previousComponent === "Closure Messages") {
      dispatch(getResetClosureMsg());
    } else if (previousComponent === "Temporary Messages") {
      dispatch(getResetTemporaryMsg());
    } else if (previousComponent === "Media Management") {
      dispatch(getResetMediaMgt());
    } else if (previousComponent === "Data Management") {
      dispatch(getResetDataManagement());
    } else if (previousComponent === "Lex Bots") {
      dispatch(resetLexBotDetail());
    }

    // Lazy load data for the selected tab
    switch (name) {
      case "Feature Flags":
        // dispatch(getFeatureFlags());
        break;
      case "Main Hours":
        dispatch(getMainHours());
        break;
      case "Closure Messages":
        dispatch(getMediaMgt());
        dispatch(getClosureMsg());
        break;
      case "Data Management":
        break;
      case "Lex Bots":
        break;
      case "Resource Manager":
        break;
      case "Media Management":
        dispatch(getMediaMgt());
        break;
      case "Temporary Messages":
        dispatch(getMediaMgt());
        dispatch(getTemporaryMsg());
        break;
      case "Callback Management":
        dispatch(getStopContactData());
        break;
      case "Routing Profiles Delete":
        // dispatch action to fetch connect data if needed
        break;
      default:
        break;
    }
  };
  const getRenderComponent = (name: string | null) => {
    switch (name) {
      case "Feature Flags": {
        return (
          <FeatureFlagsTable
            featureFlags={featureFlagsData}
            handleRefrehClick={handleRefrehClick}
          />
        );
      }
      case "Main Hours": {
        return (
          <MainHoursMaterTable
            mainHoursData={mainHoursData as Record<string, unknown>[]}
            handleRefrehClick={handleRefrehClick}
            isManager={true}
            disableAddNew={true}
            isLoading={mainHoursStatus === "pending"}
          />
        );
      }
      case "Resource Manager": {
        return <ResourceManagerTable />;
      }
      case "Closure Messages": {
        return (
          <ClosureMsgTabs
            closureMsgData={
              closureMsgData as Parameters<
                typeof ClosureMsgTabs
              >[0]["closureMsgData"]
            }
            handleRefrehClick={handleRefrehClick}
            closureTabs={
              closureTabs as Parameters<typeof ClosureMsgTabs>[0]["closureTabs"]
            }
            filteredItem={
              !closureMsgReset && closureMsgFilterItem.length === 0
                ? (CLOSUREMSG_DEFAULT_FILTER_ITEMS as string[])
                : (closureMsgFilterItem as string[])
            }
            setFilteredItem={
              getClosureMsgFilterItem as unknown as (items: string[]) => void
            }
            closureMsgReset={closureMsgReset}
            business={
              business as Parameters<typeof ClosureMsgTabs>[0]["business"]
            }
            languages={
              languages as Parameters<typeof ClosureMsgTabs>[0]["languages"]
            }
            globalConfigUi={
              globalConfigUi as unknown as Record<string, unknown>
            }
            // onNotificationChange={} // subscription to refresh snackbar state -> setSetSnackbar(event)
          />
        );
      }
      case "Data Management": {
        return <DataManagementTabs />;
      }
      case "Lex Bots": {
        return <LexBotsTable />;
      }
      case "Media Management": {
        return (
          <MediaMgtTabs
            mediaMgtData={
              mediaMgtData as unknown as Parameters<
                typeof MediaMgtTabs
              >[0]["mediaMgtData"]
            }
            handleRefrehClick={handleRefrehClick}
            filteredItem={
              !mediaMgtReset && mediaMgtFilterItem.length === 0
                ? MEDIAMSG_DEFAULT_FILTER_ITEMS
                : mediaMgtFilterItem
            }
            setFilteredItem={
              getMediaMgtFilterItem as unknown as (items: string[]) => void
            }
            mediaMgtReset={mediaMgtReset}
            languages={
              languages as Parameters<typeof MediaMgtTabs>[0]["languages"]
            }
          />
        );
      }
      case "Temporary Messages": {
        return (
          <TemporaryMsgTabs
            temporaryMsgData={temporaryMsgData}
            handleRefrehClick={handleRefrehClick}
            filteredItem={
              !temporaryMsgReset && temporaryMsgFilterItem.length === 0
                ? TEMPEMSG_DEFAULT_FILTER_ITEMS
                : temporaryMsgFilterItem
            }
            setFilteredItem={
              getTemporaryMsgFilterItem as unknown as (items: string[]) => void
            }
            temporaryMsgReset={temporaryMsgReset}
            isAdmin={admin ?? false}
          />
        );
      }
      case "Callback Management": {
        return <StopContactTable />;
      }
      case "Routing Profiles Delete": {
        return (
          <ConnectTable
            apiName="routing-profiles-summary"
            // onNotificationChange={} // subscription to refresh snackbar state -> setSetSnackbar(event)
          />
        );
      }
      case "Test SMS": {
        return <TestSms />;
      }

      default:
        return null;
    }
  };

  const snackbarMessage = useMemo(
    () =>
      snackbar.message ? <SbuxTranslate>{snackbar.message}</SbuxTranslate> : "",
    [snackbar.message],
  );

  return (
    <>
      <SbuxStackSnackbar
        open={snackbar.open}
        message={snackbarMessage}
        severity={snackbar.severity}
        autoHide={snackbar.autoHide}
        handleClose={() => {
          // Handle any necessary cleanup after snackbar closes
          if (snackbar.message === ROUTING_PROFILE_DELETION_SUCCESSFUL) {
            dispatch(resetApiData());
          }
          if (
            snackbar.message === MEDIAMGT_SAVED_SUCCESSFULLY ||
            snackbar.message === MEDIAMGT_DELETED_SUCCESSFULLY
          ) {
            dispatch(
              getMediaExpandCollapseInfo({
                mediaId: (mediaExpandCollapseInfo as Record<string, unknown>)
                  ?.mediaId as string,
                isExpanded: false,
              }),
            );
          }
          // Close the snackbar
          hideSnackbar();
        }}
      />

      <ErrorBoundary
        FallbackComponent={SbuxFallback}
        onError={handleError}
        onReset={handleRefrehClick}
      >
        <SbuxCard className="insideTabContainer">
          {status === "rejected" ? (
            <SbuxFallback />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              <SbuxDrawer
                panelData={panelData(
                  connectedInstance as Record<string, unknown> | null,
                  managerNav as Record<string, unknown>,
                  showOtherIndicator,
                  handleOtherClick,
                  handleItemClick,
                  selectedIndicator,
                )}
              >
                {getRenderComponent(selectedName)}
              </SbuxDrawer>
            </Box>
          )}
        </SbuxCard>
      </ErrorBoundary>
    </>
  );
};

export default Manager;
